import { Factory } from "lucide-react";
import type { ReactNode } from "react";
import { isInternalWorkOrderNo } from "../work-order-display";
import type { TerminalBootstrap } from "../models";
import { terminalScreenState } from "./terminal-view-model";
import {
  useTerminalWorkspace,
  type TerminalWorkspaceOptions,
} from "./useTerminalWorkspace";

type EnrollmentRenderProps = {
  terminalCode: string;
  error: string | null;
  onEnrolled: () => Promise<void>;
  onBack?: () => void;
};

type PinLoginRenderProps = {
  context: TerminalBootstrap;
  unlock?: boolean;
  onAuthenticated: () => Promise<void>;
};

type ActiveTerminalRenderProps = {
  terminalContext: TerminalBootstrap;
  terminalReconnecting: boolean;
  onMachineLogout: () => Promise<void>;
  onMachineSessionChanged: () => Promise<void>;
  onSessionMismatch: (previous: {
    sessionId: string;
    operatorId: string;
    lineId: string;
  }) => Promise<"same-owner" | "changed-owner" | "unavailable">;
};

type Props = TerminalWorkspaceOptions & {
  renderEnrollment: (props: EnrollmentRenderProps) => ReactNode;
  renderTemporaryTakeover: (props: {
    context: TerminalBootstrap;
    onClose: () => void;
    onCompleted: () => Promise<void>;
  }) => ReactNode;
  renderPinLogin: (props: PinLoginRenderProps) => ReactNode;
  renderActiveTerminal: (props: ActiveTerminalRenderProps) => ReactNode;
};

export function OperatorTerminalShell(props: Props) {
  const workspace = useTerminalWorkspace(props);
  const screen = terminalScreenState(workspace);

  if (screen.kind === "loading") {
    return (
      <TerminalState
        title={
          screen.reconnecting
            ? "Veriler yeniden eşitleniyor"
            : "Terminal doğrulanıyor"
        }
        text={
          screen.reconnecting
            ? "Bağlantı geri geldi. Makinenin güncel işi ve sayaçları sunucudan yeniden alınıyor."
            : "Makine eşleşmesi ve vardiya bilgileri sunucudan alınıyor."
        }
      />
    );
  }
  if (screen.kind === "offline") {
    return (
      <TerminalState
        title="Sunucuya ulaşılamıyor"
        text="Sunucu kimliği doğrulanmadan kayıt ekranı açılamaz. Cihazdaki taslaklar, son ölçümler ve bekleyen işlemler silinmedi; bağlantı ve aynı operatör doğrulanınca kendi kapsamında açılır."
        actionLabel="Şimdi yeniden dene"
        onAction={() => void workspace.refresh()}
        secondaryActionLabel={props.onBack ? "Önceki ekrana dön" : undefined}
        onSecondaryAction={props.onBack}
      />
    );
  }
  if (screen.kind === "enrollment") {
    return props.renderEnrollment({
      terminalCode: workspace.terminalCode,
      error: workspace.failure?.message ?? null,
      onEnrolled: workspace.refresh,
      onBack: props.onBack,
    });
  }
  if (screen.kind === "unpaired") {
    return (
      <TerminalState
        title="Terminal bir makineye eşlenmemiş"
        text="Bu tablet üretim kaydı açamaz. Vardiya amiri veya sistem yöneticisi terminali doğru makineyle eşlemelidir."
        actionLabel="Eşleşmeyi yenile"
        onAction={() => void workspace.refresh()}
        secondaryActionLabel={props.onBack ? "Önceki ekrana dön" : undefined}
        onSecondaryAction={props.onBack}
        tertiaryActionLabel="Teknik destek"
        onTertiaryAction={() =>
          props.notify(
            `Teknik desteğe terminal kimliğini bildirin: ${workspace.terminalCode}`,
          )
        }
      />
    );
  }
  if (screen.kind === "not-found") {
    return (
      <TerminalState
        title="Terminal kullanıma hazır değil"
        text="Terminal kaydı bulunamadı veya yetkili kullanıcı tarafından pasife alınmış."
        actionLabel="Yeniden dene"
        onAction={() => void workspace.refresh()}
        secondaryActionLabel={props.onBack ? "Önceki ekrana dön" : undefined}
        onSecondaryAction={props.onBack}
        tertiaryActionLabel="Teknik destek"
        onTertiaryAction={() =>
          props.notify(
            `Teknik desteğe terminal kimliğini bildirin: ${workspace.terminalCode || "tanımsız"}`,
          )
        }
      />
    );
  }
  if (screen.kind === "setup") {
    return (
      <TerminalState
        title={
          workspace.terminalCode
            ? "Terminal açılamadı"
            : "Terminal kurulumu gerekli"
        }
        text={workspace.failure?.message ?? "Terminal doğrulanamadı."}
        actionLabel={workspace.leaveTerminalLabel}
        actionDisabled={workspace.openingSetup}
        onAction={() => void workspace.leaveTerminal()}
        secondaryActionLabel="Yeniden dene"
        onSecondaryAction={() => void workspace.refresh()}
        assistance={
          workspace.canOpenSetup && !props.onBack
            ? "Sistem yöneticisi ekranında Üretim → Terminal ve PIN → Terminal eşleme yolunu açın ve “Bu tablet” seçeneğini kullanın."
            : undefined
        }
      />
    );
  }
  if (screen.kind === "unassigned") {
    return (
      <>
        <TerminalState
          title="Bu makine ve vardiya için atama yok"
          text={`${screen.context.terminal.lineName ?? screen.context.terminal.displayName} · ${screen.context.shift.shiftCode}. Normal giriş ve üretim kaydı kapalıdır; vardiya amiri geçici devralmayı onaylayabilir.`}
          carriedRun={screen.context.carriedActiveRun}
          actionLabel={
            workspace.assignmentRefreshPending
              ? "Atamalar kontrol ediliyor…"
              : "Atamayı yenile"
          }
          actionDisabled={workspace.assignmentRefreshPending}
          onAction={() => void workspace.refreshAssignments()}
          secondaryActionLabel="Vardiya amiriyle operatör ata"
          onSecondaryAction={workspace.openTemporaryTakeover}
          tertiaryActionLabel="Teknik destek"
          onTertiaryAction={() =>
            props.notify(
              `Teknik desteğe terminal kimliğini bildirin: ${screen.context.terminal.terminalCode}`,
            )
          }
          statusMessage={workspace.assignmentRefreshMessage}
        />
        {workspace.temporaryTakeoverOpen
          ? props.renderTemporaryTakeover({
              context: screen.context,
              onClose: workspace.closeTemporaryTakeover,
              onCompleted: workspace.refresh,
            })
          : null}
      </>
    );
  }
  if (screen.kind === "login") {
    return props.renderPinLogin({
      context: screen.context,
      onAuthenticated: workspace.refresh,
    });
  }
  if (screen.kind === "unlock") {
    return props.renderPinLogin({
      context: screen.context,
      unlock: true,
      onAuthenticated: workspace.refresh,
    });
  }
  return props.renderActiveTerminal({
    terminalContext: screen.context,
    terminalReconnecting: screen.reconnecting,
    onMachineLogout: workspace.machineLogout,
    onMachineSessionChanged: workspace.refresh,
    onSessionMismatch: workspace.recoverTerminalSession,
  });
}

export function TerminalState({
  title,
  text,
  carriedRun,
  actionLabel,
  actionDisabled,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  tertiaryActionLabel,
  onTertiaryAction,
  statusMessage,
  assistance,
}: {
  title: string;
  text: string;
  carriedRun?: TerminalBootstrap["carriedActiveRun"];
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  tertiaryActionLabel?: string;
  onTertiaryAction?: () => void;
  statusMessage?: string;
  assistance?: string;
}) {
  const carriedActivity = carriedRun?.planItemKind === "ACTIVITY";
  return (
    <div className="operator-terminal-gate">
      <Factory size={42} />
      <h1>{title}</h1>
      <p>{text}</p>
      {carriedRun ? (
        <section
          className="operator-terminal-carried-run"
          aria-label={
            carriedActivity ? "Devam eden planlı faaliyet" : "Devam eden iş"
          }
        >
          <small>
            {carriedActivity
              ? "DEVAM EDEN PLANLI FAALİYET"
              : "DEVAM EDEN İŞ"}{" "}
            — KAYIT GİRİŞİ KİLİTLİ
          </small>
          <strong>
            {isInternalWorkOrderNo(carriedRun.workOrderNo)
              ? carriedRun.productName
              : carriedRun.workOrderNo}
          </strong>
          {carriedActivity || isInternalWorkOrderNo(carriedRun.workOrderNo) ? null : (
            <span>{carriedRun.productName}</span>
          )}
          <em>
            {carriedActivity
              ? carriedRun.openDowntimeStartedAt
                ? "Duruş sayacı devam ediyor"
                : "Faaliyet sayacı devam ediyor"
              : `Ürün kodu ${carriedRun.productCode} · ${carriedRun.openDowntimeStartedAt ? "Duruş sayacı devam ediyor" : "Üretim sayacı devam ediyor"}`}
          </em>
        </section>
      ) : null}
      <div className="operator-terminal-gate-actions">
        {actionLabel ? (
          <button type="button" disabled={actionDisabled} onClick={onAction}>
            {actionLabel}
          </button>
        ) : null}
        {secondaryActionLabel ? (
          <button type="button" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </button>
        ) : null}
        {tertiaryActionLabel ? (
          <button type="button" onClick={onTertiaryAction}>
            {tertiaryActionLabel}
          </button>
        ) : null}
      </div>
      {statusMessage ? (
        <p
          className="operator-terminal-gate-status"
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </p>
      ) : null}
      {assistance ? (
        <small>{assistance}</small>
      ) : tertiaryActionLabel ? null : (
        <small>
          Teknik destek için terminal kimliğini ve makine adını vardiya amirine
          bildirin.
        </small>
      )}
    </div>
  );
}
